import React, { useState } from 'react';
import { Send, Calendar, CheckCircle2 } from 'lucide-react';

const SERVICE_OPTIONS = [
  { value: 'sr-leadership-hiring', label: 'Sr. Leadership Hiring' },
  { value: 'ic-sales-role', label: 'IC Sales Role' },
  { value: 'revenue-ops-crm', label: 'Revenue Operations and CRM Admins' },
  { value: 'hr-it-recruitment', label: 'HR - IT Recruitment' },
  { value: 'product-manager', label: 'Product Manager' },
  { value: 'email-marketer', label: 'Email Marketer' },
  { value: 'event-marketing-manager', label: 'Event Marketing Manager' },
  { value: 'others', label: 'Others' },
];

const TIME_SLOTS = [
  { value: '09:00', label: '09:00 AM' }, { value: '09:30', label: '09:30 AM' },
  { value: '10:00', label: '10:00 AM' }, { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' }, { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' }, { value: '14:00', label: '02:00 PM' },
  { value: '14:30', label: '02:30 PM' }, { value: '15:00', label: '03:00 PM' },
  { value: '15:30', label: '03:30 PM' }, { value: '16:00', label: '04:00 PM' },
  { value: '16:30', label: '04:30 PM' }, { value: '17:00', label: '05:00 PM' },
];

const inputClass = "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all";

const INITIAL_FORM = {
  name: '', email: '', companyWebsite: '', phone: '',
  serviceInterest: 'sr-leadership-hiring', wantExpertCall: 'yes',
  preferredDate: '', preferredTime: '', message: ''
};

export const ConsultationFormSection = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <section id="consultation-form" className="py-20 lg:py-28 bg-muted px-4 sm:px-6 lg:px-8" data-testid="consultation-form-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <Calendar className="w-10 h-10 text-accent mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Set Up a Free 30-Minute Consultation</h2>
          <p className="text-muted-foreground text-lg">Let's discuss your hiring needs and how we can help build your revenue team.</p>
        </div>
        <div className="card-depth rounded-2xl p-8 lg:p-10">
          {submitted ? (
            <div className="flex flex-col items-center gap-6 text-center py-12" data-testid="consult-success">
              <CheckCircle2 className="w-16 h-16 text-accent" />
              <h3 className="text-2xl font-bold text-foreground">Consultation Requested!</h3>
              <p className="text-muted-foreground max-w-md">Thank you! We'll confirm your consultation slot within 24 hours.</p>
              <button onClick={() => setSubmitted(false)}
                className="rounded-lg border border-border px-6 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors">
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultation-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required className={inputClass} placeholder="Your name" data-testid="consult-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} placeholder="you@company.com" data-testid="consult-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Company Website *</label>
                  <input type="url" name="companyWebsite" value={form.companyWebsite} onChange={handleChange} required className={inputClass} placeholder="https://yourcompany.com" data-testid="consult-website" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Phone (Optional)</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="+91 98765 43210" data-testid="consult-phone" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Are you hiring for active roles in *</label>
                <select name="serviceInterest" value={form.serviceInterest} onChange={handleChange} required data-testid="consult-service" className={inputClass}>
                  {SERVICE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Would you like to speak with our expert to discuss future hiring methodology? *</label>
                <select name="wantExpertCall" value={form.wantExpertCall} onChange={handleChange} required data-testid="consult-expert" className={inputClass}>
                  <option value="yes">Yes, I'd like an expert consultation</option>
                  <option value="no">No, just submit my inquiry</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Preferred Date for Meeting *</label>
                  <input type="date" name="preferredDate" value={form.preferredDate} onChange={handleChange} required
                    min={new Date().toISOString().split('T')[0]} className={inputClass} data-testid="consult-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Preferred Time *</label>
                  <select name="preferredTime" value={form.preferredTime} onChange={handleChange} required data-testid="consult-time" className={inputClass}>
                    <option value="">Select a time</option>
                    {TIME_SLOTS.map((slot) => <option key={slot.value} value={slot.value}>{slot.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Additional Notes (Optional)</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={3} className={`${inputClass} resize-y`}
                  placeholder="Any specific topics you'd like to discuss..." data-testid="consult-message" />
              </div>
              <button type="submit" disabled={loading} data-testid="consult-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-base px-6 py-3.5 hover:bg-accent/90 disabled:opacity-50 transition-all">
                {loading ? 'Scheduling...' : 'Schedule My Free Consultation'} <Send className="w-4 h-4" />
              </button>
              <p className="text-xs text-muted-foreground text-center">We'll confirm your 45-minute meeting slot within 24 hours.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
